import React, { useEffect, useState } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Lock, ShieldAlert, Eye, EyeOff } from "lucide-react";

const INDIA_CATEGORIES = ["politics", "breaking", "foreign-affairs", "defence", "economy", "tech"];
const COUNTRIES = [
  { code: "us", name: "United States" }, { code: "gb", name: "United Kingdom" },
  { code: "ru", name: "Russia" }, { code: "cn", name: "China" }, { code: "fr", name: "France" },
  { code: "de", name: "Germany" }, { code: "il", name: "Israel" }, { code: "ir", name: "Iran" },
  { code: "ua", name: "Ukraine" }, { code: "jp", name: "Japan" }, { code: "kr", name: "South Korea" },
  { code: "sa", name: "Saudi Arabia" }, { code: "ae", name: "UAE" }, { code: "tr", name: "Turkey" },
  { code: "pk", name: "Pakistan" }, { code: "bd", name: "Bangladesh" }, { code: "lk", name: "Sri Lanka" },
  { code: "mv", name: "Maldives" }, { code: "np", name: "Nepal" }, { code: "af", name: "Afghanistan" },
  { code: "au", name: "Australia" }, { code: "ca", name: "Canada" }, { code: "br", name: "Brazil" },
  { code: "za", name: "South Africa" }, { code: "eg", name: "Egypt" }, { code: "id", name: "Indonesia" },
  { code: "sg", name: "Singapore" }, { code: "vn", name: "Vietnam" }, { code: "in", name: "India" },
];

export default function AuleloVault() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [audit, setAudit] = useState([]);
  const [rotateForm, setRotateForm] = useState({ current: "", next: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        auleloApi.get("/config"),
        auleloApi.get("/audit-log"),
      ]);
      setCfg(c.data);
      setAudit(a.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const { data } = await auleloApi.patch("/config", {
        agent_name: cfg.agent_name,
        india_categories: cfg.india_categories,
        international_countries: cfg.international_countries,
        bilateral_pair: cfg.bilateral_pair,
        podcast_voice: cfg.podcast_voice,
        podcast_model: cfg.podcast_model,
        voice_india: cfg.voice_india,
        voice_world: cfg.voice_world,
        voice_china: cfg.voice_china,
        voice_intro: cfg.voice_intro,
        auto_podcast_enabled: cfg.auto_podcast_enabled,
        auto_podcast_hour_ist: cfg.auto_podcast_hour_ist,
        auto_podcast_email: cfg.auto_podcast_email,
        summary_format: cfg.summary_format,
      });
      setCfg(data);
      toast.success("Vault sealed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const rotate = async () => {
    if (rotateForm.next !== rotateForm.confirm) { toast.error("New passkeys don't match"); return; }
    if (rotateForm.next.length < 6) { toast.error("Min 6 characters"); return; }
    setRotating(true);
    try {
      await auleloApi.post("/rotate-passkey", { current_passkey: rotateForm.current, new_passkey: rotateForm.next });
      toast.success("Passkey rotated · re-authenticate next session");
      setRotateForm({ current: "", next: "", confirm: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rotation failed"); }
    finally { setRotating(false); }
  };

  const toggleCat = (cat) => {
    const cur = cfg.india_categories || [];
    setCfg({ ...cfg, india_categories: cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat] });
  };
  const toggleCountry = (code) => {
    const cur = cfg.international_countries || [];
    setCfg({ ...cfg, international_countries: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code] });
  };

  if (!cfg) return null;

  return (
    <div className="space-y-10" data-testid="aulelo-vault">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// vault</div>
        <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Controls &amp; keys.</h1>
        <p className="text-zinc-400 mt-2 font-mono text-sm">Tune what the agent watches, change its name, rotate the passkey.</p>
      </div>

      {/* Agent identity */}
      <Section title="Agent identity">
        <Row label="Agent name">
          <Input value={cfg.agent_name || ""} onChange={(e) => setCfg({ ...cfg, agent_name: e.target.value })} className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono w-72" data-testid="aulelo-agent-name-input" />
        </Row>
      </Section>

      {/* Passkey rotation */}
      <Section title="Passkey">
        <div className="border border-yellow-700/40 bg-yellow-900/10 p-4 text-xs text-yellow-200/90 flex gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
          <div>
            After rotation, the new passkey becomes the URL slug (e.g. <code className="text-yellow-300">/Newkey/</code>). The old URL stops working immediately.
          </div>
        </div>
        <Row label="Current passkey">
          <div className="flex gap-1 w-72">
            <Input type={showCurrent ? "text" : "password"} value={rotateForm.current} onChange={(e) => setRotateForm({ ...rotateForm, current: e.target.value })} className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid="aulelo-rotate-current" />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="border border-emerald-700/40 px-2 text-emerald-300">{showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
          </div>
        </Row>
        <Row label="New passkey (6+ chars)">
          <Input type="password" value={rotateForm.next} onChange={(e) => setRotateForm({ ...rotateForm, next: e.target.value })} className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono w-72" data-testid="aulelo-rotate-next" />
        </Row>
        <Row label="Confirm new passkey">
          <Input type="password" value={rotateForm.confirm} onChange={(e) => setRotateForm({ ...rotateForm, confirm: e.target.value })} className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono w-72" data-testid="aulelo-rotate-confirm" />
        </Row>
        <Button onClick={rotate} disabled={rotating || !rotateForm.current || !rotateForm.next} data-testid="aulelo-rotate-submit" className="mt-2 rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px] h-10 px-5">
          {rotating ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-2" />}
          Rotate passkey
        </Button>
      </Section>

      {/* India categories */}
      <Section title="India domestic — categories">
        <div className="flex flex-wrap gap-2">
          {INDIA_CATEGORIES.map((c) => {
            const on = (cfg.india_categories || []).includes(c);
            return (
              <button key={c} onClick={() => toggleCat(c)} data-testid={`aulelo-cat-${c}`}
                className={`font-mono text-[11px] uppercase tracking-widest px-3 py-2 border ${on ? "border-emerald-500 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 text-zinc-500 hover:text-emerald-300"}`}>
                {c}
              </button>
            );
          })}
        </div>
      </Section>

      {/* International countries */}
      <Section title="International — countries to watch">
        <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto scrollbar-thin p-1">
          {COUNTRIES.map((c) => {
            const on = (cfg.international_countries || []).includes(c.code);
            return (
              <button key={c.code} onClick={() => toggleCountry(c.code)} data-testid={`aulelo-country-${c.code}`}
                className={`font-mono text-[11px] uppercase tracking-widest px-3 py-2 border flex items-center gap-2 ${on ? "border-emerald-500 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 text-zinc-500 hover:text-emerald-300"}`}>
                <img alt={c.code} src={`https://flagcdn.com/16x12/${c.code}.png`} className="w-4 h-3" />
                {c.name}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Bilateral pair */}
      <Section title="Bilateral — pair of focus">
        <Row label="Country A">
          <Select value={cfg.bilateral_pair?.a || "in"} onValueChange={(v) => setCfg({ ...cfg, bilateral_pair: { ...cfg.bilateral_pair, a: v } })}>
            <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-100 w-72 h-10 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-bilat-a"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100 max-h-72">{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code} className="rounded-none font-mono">{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Country B">
          <Select value={cfg.bilateral_pair?.b || "us"} onValueChange={(v) => setCfg({ ...cfg, bilateral_pair: { ...cfg.bilateral_pair, b: v } })}>
            <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-100 w-72 h-10 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-bilat-b"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100 max-h-72">{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code} className="rounded-none font-mono">{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
      </Section>

      {/* Podcast prefs */}
      <Section title="Podcast voices · per region">
        <Row label="India desk">
          <VoiceSelect testid="aulelo-voice-india" value={cfg.voice_india || cfg.podcast_voice} onChange={(v) => setCfg({ ...cfg, voice_india: v })} />
        </Row>
        <Row label="World desk">
          <VoiceSelect testid="aulelo-voice-world" value={cfg.voice_world || cfg.podcast_voice} onChange={(v) => setCfg({ ...cfg, voice_world: v })} />
        </Row>
        <Row label="China watch">
          <VoiceSelect testid="aulelo-voice-china" value={cfg.voice_china || cfg.podcast_voice} onChange={(v) => setCfg({ ...cfg, voice_china: v })} />
        </Row>
        <Row label="Intro / outro">
          <VoiceSelect testid="aulelo-voice-intro" value={cfg.voice_intro || cfg.podcast_voice} onChange={(v) => setCfg({ ...cfg, voice_intro: v })} />
        </Row>
        <Row label="Default (fallback)">
          <VoiceSelect testid="aulelo-voice-select" value={cfg.podcast_voice} onChange={(v) => setCfg({ ...cfg, podcast_voice: v })} />
        </Row>
        <Row label="Model">
          <Select value={cfg.podcast_model} onValueChange={(v) => setCfg({ ...cfg, podcast_model: v })}>
            <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-100 w-72 h-10 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-model-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100">
              <SelectItem value="tts-1" className="rounded-none font-mono">tts-1 · fast</SelectItem>
              <SelectItem value="tts-1-hd" className="rounded-none font-mono">tts-1-hd · podcast quality</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </Section>

      {/* Auto-podcast schedule */}
      <Section title="Auto-podcast — morning brief">
        <Row label="Enabled">
          <button
            onClick={() => setCfg({ ...cfg, auto_podcast_enabled: !cfg.auto_podcast_enabled })}
            data-testid="aulelo-auto-toggle"
            className={`px-4 py-2 border font-mono text-[11px] uppercase tracking-widest ${
              cfg.auto_podcast_enabled ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-800 text-zinc-500"
            }`}
          >
            {cfg.auto_podcast_enabled ? "ON · daily" : "OFF"}
          </button>
        </Row>
        <Row label="Time (IST, hour 0-23)">
          <Input
            type="number" min={0} max={23}
            value={cfg.auto_podcast_hour_ist ?? 6}
            onChange={(e) => setCfg({ ...cfg, auto_podcast_hour_ist: Math.max(0, Math.min(23, Number(e.target.value))) })}
            data-testid="aulelo-auto-hour"
            className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono w-32"
          />
        </Row>
        <Row label="Email link to (optional)">
          <Input
            type="email"
            value={cfg.auto_podcast_email || ""}
            onChange={(e) => setCfg({ ...cfg, auto_podcast_email: e.target.value })}
            placeholder="ms.minister@gov.in"
            data-testid="aulelo-auto-email"
            className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono w-72"
          />
        </Row>
        <div className="text-[11px] text-zinc-500 font-mono pl-1 pt-1">
          Runs once a day. The job triggers ~30 minutes after the IST hour (UTC-aligned cron) and emails a deep-link to today's MP3.
        </div>
      </Section>

      {/* Format */}
      <Section title="Brief format">
        <Row label="Section style">
          <Select value={cfg.summary_format || "bullets"} onValueChange={(v) => setCfg({ ...cfg, summary_format: v })}>
            <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-100 w-72 h-10 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-summary-format"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100">
              <SelectItem value="bullets" className="rounded-none font-mono">bullets · scannable</SelectItem>
              <SelectItem value="paragraph" className="rounded-none font-mono">paragraph · prose</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </Section>

      {/* Audit log */}
      <Section title="Access audit (last 50)">
        <div className="border border-zinc-800 bg-black max-h-72 overflow-y-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 sticky top-0">
              <tr><th className="text-left p-2 font-mono uppercase tracking-widest text-zinc-500 text-[10px]">When</th><th className="text-left p-2 font-mono uppercase tracking-widest text-zinc-500 text-[10px]">IP</th><th className="text-left p-2 font-mono uppercase tracking-widest text-zinc-500 text-[10px]">Result</th></tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-zinc-900" data-testid={`aulelo-audit-${a.id}`}>
                  <td className="p-2 font-mono text-zinc-400">{String(a.at).slice(0, 19).replace("T", " ")}</td>
                  <td className="p-2 font-mono text-zinc-500">{a.ip}</td>
                  <td className={`p-2 font-mono ${a.success ? "text-emerald-400" : "text-red-400"}`}>{a.success ? "✓ ok" : "✗ " + a.reason}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={3} className="p-3 text-zinc-600 text-center font-mono">no entries</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="aulelo-vault-save" className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px] h-11 px-8">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
          Save vault
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border-t border-emerald-900/40 pt-6 space-y-3">
    <h2 className="font-mono text-xl font-black tracking-tight text-emerald-200">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);
const Row = ({ label, children }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-zinc-900 pb-3">
    <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">{label}</Label>
    <div>{children}</div>
  </div>
);

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const VoiceSelect = ({ value, onChange, testid }) => (
  <Select value={value || "shimmer"} onValueChange={onChange}>
    <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-100 w-72 h-10 font-mono uppercase tracking-widest text-[11px]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100">
      {VOICES.map((v) => (
        <SelectItem key={v} value={v} className="rounded-none font-mono uppercase tracking-widest">{v}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);
